import axios from "axios";
import {
  Conversation,
  Message,
  Agent,
  AIHandoff,
  ConversationFilters,
  SendMessagePayload,
} from "../types";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

class ApiService {
  private api;

  constructor() {
    this.api = axios.create({
      baseURL: `${API_BASE_URL}/api`,
      headers: {
        "Content-Type": "application/json",
      },
    });

    // Add auth token to requests
    this.api.interceptors.request.use((config) => {
      const token = localStorage.getItem("auth_token");
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    // Handle auth errors
    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem("auth_token");
          window.location.href = "/login";
        }
        return Promise.reject(error);
      }
    );
  }

  // Auth
  async login(email: string, password: string) {
    const response = await this.api.post("/auth/login", { email, password });
    if (response.data.token) {
      localStorage.setItem("auth_token", response.data.token);
    }
    console.log("login() from frontend");
    return response.data;
  }

  async logout() {
    await this.api.post("/auth/logout");
    localStorage.removeItem("auth_token");
  }

  async getCurrentUser() {
    const response = await this.api.get("/auth/me");
    return response.data;
  }

  // Conversations
  async getConversations(
    filters?: ConversationFilters
  ): Promise<Conversation[]> {
    const response = await this.api.get("/conversations", { params: filters });
    return response.data;
  }

  async getConversation(
    id: string
  ): Promise<Conversation & { messages: Message[] }> {
    const response = await this.api.get(`/conversations/${id}`);
    return response.data;
  }

  async createConversation(data: Partial<Conversation>): Promise<Conversation> {
    const response = await this.api.post("/conversations", data);
    return response.data;
  }

  async updateConversation(
    id: string,
    data: Partial<Conversation>
  ): Promise<Conversation> {
    const response = await this.api.put(`/conversations/${id}`, data);
    return response.data;
  }

  async assignAgent(
    conversationId: string,
    agentId: string
  ): Promise<Conversation> {
    const response = await this.api.put(
      `/conversations/${conversationId}/assign`,
      {
        agent_id: agentId,
      }
    );
    return response.data;
  }

  async archiveConversation(id: string): Promise<Conversation> {
    const response = await this.api.delete(`/conversations/${id}`);
    return response.data;
  }

  // Messages
  async getMessages(conversationId: string): Promise<Message[]> {
    const response = await this.api.get(
      `/conversations/${conversationId}/messages`
    );
    return response.data;
  }

  async sendMessage(
    conversationId: string,
    data: SendMessagePayload
  ): Promise<Message> {
    const response = await this.api.post(
      `/conversations/${conversationId}/messages`,
      data
    );
    return response.data;
  }

  // AI Integration
  async requestAIHandoff(
    conversationId: string,
    reason: string
  ): Promise<AIHandoff> {
    const response = await this.api.post("/ai/handoff", {
      conversation_id: conversationId,
      reason,
    });
    return response.data;
  }

  async resumeAI(conversationId: string): Promise<any> {
    const response = await this.api.post("/ai/resume", {
      conversation_id: conversationId,
    });
    return response.data;
  }

  async getAIStatus(conversationId: string): Promise<any> {
    const response = await this.api.get(`/ai/status/${conversationId}`);
    return response.data;
  }

  async takeoverFromAI(
    conversationId: string,
    agentId: string,
    reason?: string
  ): Promise<any> {
    const response = await this.api.post(`/ai/takeover/${conversationId}`, {
      agent_id: agentId,
      reason,
    });
    return response.data;
  }

  async getAIHandoffs(): Promise<AIHandoff[]> {
    const response = await this.api.get("/ai/handoffs");
    return response.data;
  }

  // Agents
  async getAgents(): Promise<Agent[]> {
    const response = await this.api.get("/agents");
    return response.data;
  }

  async updateAgentStatus(agentId: string, status: string): Promise<Agent> {
    const response = await this.api.put(`/agents/${agentId}/status`, {
      status,
    });
    return response.data;
  }
}

export const apiService = new ApiService();
export default apiService;
